var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ensureIsError, rejectWithResErr } from "@iscr/xAppLib/helpers/rejectWithResErr";
import { ErrorMessage } from "@iscr/xAppLib/UIelems/ErrorMessage";
import { Button, Dimmer, Icon, Loader, Modal, Table } from "semantic-ui-react";
import { createPortal } from "react-dom";
import { useDisclosure } from "@iscr/xAppLib/Hooks/useDisclosure";
import hi_model from "@iscr/models/hi/hi_model";
import { toDictionary } from "xAppLib/helpers/toDictionary";
import { AsyncButton } from "views/UIelems/AsyncButton";
import Alert from "views/NUI/Alert";
const FIELD_LABELS = toDictionary(hi_model.FIELDS, f => f.name, f => f.label);
function ResolveDemographicMismatchAlert({ uid, actions, for_script = false, onCancel, onUpdated, }) {
    const mismatch = useQuery({
        queryKey: ["admin", "audit", "get-demographic-mismatch", { uid }],
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            return hi_model
                .get_demo_conflicts({ uid })
                .then(rejectWithResErr, ensureIsError)
                .then(res => res.data);
        }),
        placeholderData: data => data,
    });
    const syncIhiToPatient = useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            if (has_diff) {
                const confirmed = yield app.confirm("Confirm sync IHI to user", _jsx(ConfirmSync, { for_script: for_script }), {
                    destructive: true,
                    yes: "Continue",
                    no: "Cancel",
                });
                if (confirmed !== "yes") {
                    return;
                }
            }
            yield hi_model.sync_ihi_to_patient({ uid }).then(rejectWithResErr, ensureIsError);
            return yield Promise.all([mismatch.refetch(), onUpdated(uid)]);
        }),
    });
    const data = mismatch.data;
    if (mismatch.isPending) {
        return _jsx(_Fragment, { children: "Loading..." });
    }
    const has_diff = Boolean(data && Object.values(data).some(cmp => cmp.diff));
    return (_jsxs(_Fragment, { children: [_jsx(ErrorMessage, { header: "Loading demographic info", error: mismatch.error, onRetry: mismatch.refetch }), _jsx(ErrorMessage, { header: "Syncing profile", error: syncIhiToPatient.error, onRetry: () => syncIhiToPatient.mutateAsync() }), !!data && (_jsxs("div", { className: "relative", children: [_jsx(Dimmer, { active: mismatch.isFetching, inverted: true, children: _jsx(Loader, {}) }), _jsxs(Table, { striped: true, className: "loading", children: [_jsx(Table.Header, { children: _jsxs(Table.Row, { children: [has_diff && _jsx(Table.HeaderCell, { children: "\u00A0" }), _jsx(Table.HeaderCell, { children: "Property" }), _jsx(Table.HeaderCell, { children: "Patient" }), _jsx(Table.HeaderCell, { children: "IHI" })] }) }), _jsx(Table.Body, { children: Object.entries(data).map(([key, compare]) => {
                                    var _a;
                                    return (_jsxs(Table.Row, { error: compare.diff, children: [has_diff && (_jsx(Table.Cell, { collapsing: true, children: compare.diff && _jsx(Icon, { name: "warning sign", color: "red" }) })), _jsxs(Table.Cell, { children: [(_a = FIELD_LABELS[key]) !== null && _a !== void 0 ? _a : key, " "] }), _jsx(Table.Cell, { children: compare.source }), _jsx(Table.Cell, { children: compare.target })] }, key));
                                }) })] })] })), actions &&
                createPortal(_jsxs(_Fragment, { children: [_jsx(Button, { secondary: true, type: "button", onClick: onCancel, children: "Cancel" }), _jsx(AsyncButton, { disabled: mismatch.isFetching, primary: true, type: "button", onClick: () => syncIhiToPatient.mutateAsync(), children: has_diff ? "Sync to patient" : "Resolve alert" })] }), actions)] }));
}
function ConfirmSync({ for_script }) {
    return (_jsxs(_Fragment, { children: [_jsx("p", { children: "Please confirm with the patient all details are correct before continuing." }), for_script ? (_jsx(Alert, { warning: true, children: "You must manually sync the user profile to the script before attempting to re-issue eScript." })) : (_jsx(Alert, { info: true, children: "If the patient has any stuck eScripts due to this error, you must manually sync the user profile to the script before attempting to re-issue eScript." }))] }));
}
export function ResolveDemographicMismatchAlertPopper(_a) {
    var { refresh } = _a, props = __rest(_a, ["refresh"]);
    const [actionsRef, setActionsRef] = useState(null);
    const { onOpen, onClose, isOpen } = useDisclosure();
    if (!props.uid) {
        return null;
    }
    const trigger = _jsx(Button, { type: "button", color: "orange", content: "Review demographic mismatch" });
    return (_jsxs(Modal, { trigger: trigger, size: "large", open: isOpen, onClose: onClose, onOpen: onOpen, closeIcon: true, children: [_jsx(Modal.Header, { closeButton: true, children: "Resolve demographic mismatch" }), _jsx(Modal.Content, { scrolling: true, children: _jsx(ResolveDemographicMismatchAlert, Object.assign({}, props, { actions: actionsRef, onCancel: onClose, onUpdated: () => __awaiter(this, void 0, void 0, function* () {
                        yield (refresh === null || refresh === void 0 ? void 0 : refresh());
                        onClose();
                    }) })) }), _jsx(Modal.Actions, { children: _jsx("span", { className: "flex flex-row flex-1 gap-2", ref: setActionsRef }) })] }));
}
